import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, Loader2, MapPin, PackageCheck, RefreshCw, SkipForward, Undo2 } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { Field, inputClass } from '../../../../components/ui/Field';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { ProgressBar } from '../../../../components/ui/ProgressBar';
import { CandidatesSheet } from '../../components/CandidatesSheet';
import { CodeText } from '../../components/CodeText';
import { OperatorActionBar } from '../../components/OperatorActionBar';
import { OperatorScanPanel } from '../../components/OperatorScanPanel';
import { OperatorSheet } from '../../components/OperatorSheet';
import { QuantityStepper } from '../../components/QuantityStepper';
import { taskEntered } from '../../../../features/operator/operatorDeviceSlice';
import { OPERATOR_FLOW } from '../../../../features/operator/operatorConstants';
import { countTarget, unitCode, unitsLabel } from '../../../../features/operator/operatorCodes';
import { SCAN_SOURCE } from '../../../../features/operator/operatorScanGate';
import { orderCardTitle, selectEnabledFlows, selectOrderReserveReady } from '../../../../features/operator/operatorUtils';
import {
  choosePickingOption,
  fetchPickingOrder,
  pickLine,
  reservePickingOrder,
  scanPickingCode,
  shortPickLine,
  submitPick,
  submitReserve,
  submitShortPick,
  submitUnpick,
  unpickLine,
} from '../../../../features/operatorPicking/operatorPickingThunks';
import {
  pickingEntered,
  pickingLeft,
  pickingLineChosen,
  pickingLineSkipped,
  pickingResolutionDismissed,
  pickingStepBack,
  selectPickingAccepting,
  selectPickingLine,
} from '../../../../features/operatorPicking/operatorPickingSlice';
import {
  PICKING_STEP,
  canChooseLine,
  firstTarget,
  needsReserve,
  pendingOf,
  pickQuantityProblem,
  reserveNoteText,
  shortPickProblem,
  unpickableLines,
} from '../../../../features/operatorPicking/operatorPickingUtils';
import { DISCREPANCY_REASON_OPTIONS } from '../../../../features/fulfillment/fulfillmentUtils';
import { formatQuantity } from '../../../../utils/inventoryLabels';

/** Big choices for one hand, as radio buttons. */
const Choices = ({ name, options, value, onChange, testId }) => (
  <div className="grid gap-2">
    {options.map((option) => (
      <label
        key={option.value}
        data-testid={testId}
        data-value={option.value}
        className={clsx(
          'flex min-h-[48px] cursor-pointer items-center gap-3 rounded-2xl border px-4 py-2',
          value === option.value ? 'border-ninja-green bg-ninja-greenLight dark:bg-[#1f2b24]' : 'border-border'
        )}
      >
        <input
          type="radio"
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={() => onChange(option.value)}
          className="h-5 w-5 accent-ninja-green"
        />
        <span className="text-sm font-semibold text-ninja-dark dark:text-white">{option.label}</span>
      </label>
    ))}
  </div>
);

/**
 * How many to take, and from which lot: counted from 0 up to what the line
 * lacks, «0 / 2». Seeded once per scanned product: remounted by key.
 */
const PickForm = ({ orderId, line, target, product, onBack }) => {
  const dispatch = useDispatch();
  const lots = target?.lots || [];
  const [quantity, setQuantity] = useState('0');
  const [lot, setLot] = useState(() => (lots.length > 0 ? lots[0].lot : null));
  const problem = pickQuantityProblem(quantity, line, product.factor);
  const goal = countTarget(pendingOf(line), product.factor);

  const submit = () =>
    dispatch(
      submitPick({
        orderId,
        lineId: line.line_id,
        productCode: product.code,
        slotCode: target.slot_code,
        slotName: target.slot_name,
        quantity: Number(quantity),
        factor: product.factor,
        ...(lots.length > 0 ? { lot } : {}),
      })
    );

  return (
    <div data-testid="pick-form" className="space-y-4 rounded-3xl border border-border bg-white p-4 dark:bg-[#1a1a1a]">
      <QuantityStepper
        label={product.factor > 1 ? '¿Cuántas cajas sacas?' : '¿Cuántos sacas?'}
        value={quantity}
        onChange={setQuantity}
        min={0}
        max={goal ?? undefined}
        target={goal ?? undefined}
        unit={product.factor > 1 ? unitsLabel(quantity, product.factor) : 'un'}
      />
      {lots.length > 1 && (
        <Choices
          name="pick-lot"
          testId="pick-lot"
          value={lot}
          onChange={setLot}
          options={lots.map((item) => ({
            value: item.lot,
            label: `${item.lot ? `Lote ${item.lot}` : 'Sin lote'} · ${formatQuantity(item.quantity)} un`,
          }))}
        />
      )}
      <OperatorActionBar>
        <Button variant="ghost" size="lg" onClick={onBack}>
          Atrás
        </Button>
        <PendingButton
          pendingOn={pickLine}
          pendingKey={orderId}
          variant="primary"
          size="lg"
          disabled={Boolean(problem)}
          loadingLabel="Pickeando…"
          onClick={submit}
          data-testid="pick-submit"
        >
          {problem || `Pickear ${unitsLabel(quantity, product.factor)}`}
        </PendingButton>
      </OperatorActionBar>
    </div>
  );
};

/** "No está": finding less than the system says, with the reason. */
const ShortPickSheet = ({ isOpen, onClose, orderId, line, target, product }) => {
  const dispatch = useDispatch();
  const pending = useSelector((state) => state.operatorPicking.pending);
  const [found, setFound] = useState('0');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const problem = shortPickProblem({ found, reason });

  const submit = async () => {
    const action = await dispatch(
      submitShortPick({
        orderId,
        lineId: line.line_id,
        slotCode: target.slot_code,
        slotName: target.slot_name,
        found: Number(found),
        reason,
        notes: notes.trim() || undefined,
        productCode: product?.code || line.codes?.barcode || line.sku,
      })
    );
    if (shortPickLine.fulfilled.match(action)) onClose();
  };

  return (
    <OperatorSheet isOpen={isOpen} onClose={onClose} title={`No está en ${target?.slot_name || target?.slot_code || ''}`} testId="short-pick-sheet" busy={pending === 'shortPick'}>
      <div className="space-y-4">
        <QuantityStepper
          label="¿Cuántos encontraste?"
          value={found}
          onChange={setFound}
          min={0}
          max={pendingOf(line)}
          target={pendingOf(line)}
          markComplete={false}
          testId="short-pick-found"
        />
        <Choices name="short-pick-reason" testId="short-pick-reason" value={reason} onChange={setReason} options={DISCREPANCY_REASON_OPTIONS} />
        <Field label="Nota (opcional)" htmlFor="short-pick-notes">
          <input id="short-pick-notes" value={notes} onChange={(event) => setNotes(event.target.value)} className={`${inputClass} h-12 text-base`} />
        </Field>
        <PendingButton
          pendingOn={shortPickLine}
          pendingKey={orderId}
          variant="primary"
          size="lg"
          fullWidth
          disabled={Boolean(problem)}
          loadingLabel="Registrando…"
          onClick={submit}
          data-testid="short-pick-submit"
        >
          {problem || 'Registrar desvío'}
        </PendingButton>
      </div>
    </OperatorSheet>
  );
};

/** Gives picked units back to a slot, for a line picked by mistake. */
const UnpickSheet = ({ isOpen, onClose, orderId, lines }) => {
  const dispatch = useDispatch();
  const pending = useSelector((state) => state.operatorPicking.pending);
  const picked = lines.filter((line) => Number(line.prepared_quantity) > 0);
  const [lineId, setLineId] = useState(() => picked[0]?.line_id ?? null);
  const [slotCode, setSlotCode] = useState('');
  const [quantity, setQuantity] = useState('1');
  const line = picked.find((item) => item.line_id === lineId);
  const amount = Number(quantity);
  const problem = !line
    ? 'Elige la línea'
    : !slotCode.trim()
      ? 'Indica dónde lo dejas'
      : !(amount > 0) || !Number.isInteger(amount)
        ? 'Indica cuántos devuelves'
        : amount > Number(line.prepared_quantity)
          ? `Hay ${formatQuantity(line.prepared_quantity)} un pickeadas`
          : null;

  const submit = async () => {
    const action = await dispatch(submitUnpick({ orderId, lineId, slotCode: slotCode.trim(), quantity: amount }));
    if (unpickLine.fulfilled.match(action)) onClose();
  };

  return (
    <OperatorSheet isOpen={isOpen} onClose={onClose} title="Devolver lo pickeado" testId="unpick-sheet" busy={pending === 'unpick'}>
      <div className="space-y-4">
        <Choices
          name="unpick-line"
          testId="unpick-line"
          value={lineId}
          onChange={setLineId}
          options={picked.map((item) => ({
            value: item.line_id,
            label: `${item.sku} · ${formatQuantity(item.prepared_quantity)} un pickeadas`,
          }))}
        />
        <Field label="¿Dónde lo dejas? (código de la ubicación)" htmlFor="unpick-slot">
          <input
            id="unpick-slot"
            data-testid="unpick-slot"
            value={slotCode}
            onChange={(event) => setSlotCode(event.target.value)}
            autoComplete="off"
            autoCapitalize="characters"
            className={`${inputClass} h-12 text-base`}
          />
        </Field>
        <QuantityStepper label="¿Cuántos devuelves?" value={quantity} onChange={setQuantity} min={0} testId="unpick-quantity" />
        <PendingButton
          pendingOn={unpickLine}
          pendingKey={orderId}
          variant="primary"
          size="lg"
          fullWidth
          disabled={Boolean(problem)}
          loadingLabel="Devolviendo…"
          onClick={submit}
          data-testid="unpick-submit"
        >
          {problem || 'Devolver'}
        </PendingButton>
      </div>
    </OperatorSheet>
  );
};

/**
 * Reserving the order's stock, while its client's contract does not reserve on
 * entry and part of it is not reserved. Optional: the walk goes on without it.
 * Off while another write of the order waits to be confirmed — sent now, it
 * would take that write's place, and its key —; a reserve whose answer was
 * lost goes again under its own.
 */
const ReserveBlock = ({ order, lastWrite }) => {
  const dispatch = useDispatch();
  const ready = useSelector(selectOrderReserveReady);
  if (!ready || !needsReserve(order)) return null;
  const blocked = Boolean(lastWrite) && lastWrite.type !== reservePickingOrder.typePrefix;

  return (
    <section data-testid="picking-reserve" className="space-y-3 rounded-3xl border border-border bg-white p-4 dark:bg-[#1a1a1a]">
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} aria-hidden="true" className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="space-y-1">
          <p className="text-base font-bold text-ninja-dark dark:text-white">{reserveNoteText(order)}</p>
          <p className="text-sm text-text-secondary">Reservar es opcional: puedes pickear igual.</p>
        </div>
      </div>
      <PendingButton
        pendingOn={reservePickingOrder}
        pendingKey={order.id}
        variant="secondary"
        size="lg"
        fullWidth
        disabled={blocked}
        loadingLabel="Reservando…"
        onClick={() => dispatch(submitReserve({ orderId: order.id }))}
        data-testid="picking-reserve-submit"
      >
        Reservar stock
      </PendingButton>
    </section>
  );
};

/**
 * The guided picking walk through one order, line by line in route order:
 * go to the slot, scan it, scan the product, say how many, next. What is not
 * there is reported; a line can wait for the end.
 */
export const PickingWalk = () => {
  const { orderId } = useParams();
  const id = Number(orderId);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const picking = useSelector((state) => state.operatorPicking);
  const line = useSelector(selectPickingLine);
  const accepting = useSelector(selectPickingAccepting);
  const flows = useSelector(selectEnabledFlows);
  const { order, loading, error, walk, resolution, pending } = picking;
  const [sheet, setSheet] = useState(null);

  useEffect(() => {
    dispatch(pickingEntered(id));
    dispatch(taskEntered({ flow: OPERATOR_FLOW.PICKING, taskId: id }));
    dispatch(fetchPickingOrder(id));
    return () => {
      dispatch(pickingLeft());
    };
  }, [dispatch, id]);

  if (!order && loading) {
    return (
      <div className="flex justify-center py-16" data-testid="picking-loading">
        <Loader2 size={32} className="animate-spin text-ninja-green" />
      </div>
    );
  }
  if (!order && error) {
    return (
      <ErrorState title="No pudimos abrir la orden" description={error.message} onRetry={() => dispatch(fetchPickingOrder(id))}>
        <Button variant="ghost" onClick={() => navigate('/picking')}>
          Volver
        </Button>
      </ErrorState>
    );
  }
  if (!order) return null;

  const read = (code, source, alternatives) => dispatch(scanPickingCode({ code, source, alternatives }));
  const tap = (code) => read(code, SCAN_SOURCE.TAP);
  const target = walk.target || firstTarget(line);
  const atSlot = walk.step === PICKING_STEP.SLOT;
  const atScanStep = atSlot || walk.step === PICKING_STEP.PRODUCT;
  const choosable = atScanStep ? order.lines.filter((item) => canChooseLine(item, walk)) : [];
  const hasPicked = order.lines.some((item) => Number(item.prepared_quantity) > 0);
  const stuck = unpickableLines(order.lines);

  return (
    <div className="space-y-5" data-testid="picking-walk" data-step={walk.step}>
      <section className="space-y-3 rounded-3xl border border-border bg-white p-4 dark:bg-[#1a1a1a]">
        <div>
          <p className="text-xl font-bold text-ninja-dark dark:text-white">{orderCardTitle(order)}</p>
          <p className="text-sm text-text-secondary">{[order.recipient, order.company_name].filter(Boolean).join(' · ')}</p>
        </div>
        <ProgressBar
          testId="picking-progress"
          value={Number(order.prepared_units) || 0}
          max={Number(order.units) || 0}
          label={`${formatQuantity(order.prepared_units)} de ${formatQuantity(order.units)} un pickeadas`}
        />
      </section>

      <ReserveBlock order={order} lastWrite={picking.lastWrite} />

      {error && (
        <div data-testid="picking-reload-error" role="alert">
          <ErrorState variant="inline" title="No pudimos recargar la orden; puede no estar al día." description={error.message}>
            <PendingButton
              pendingOn={fetchPickingOrder}
              pendingKey={id}
              variant="secondary"
              size="lg"
              fullWidth
              icon={RefreshCw}
              loadingLabel="Recargando…"
              onClick={() => dispatch(fetchPickingOrder(id))}
              data-testid="picking-reload-error-retry"
            >
              Recargar la orden
            </PendingButton>
          </ErrorState>
        </div>
      )}

      {walk.step === PICKING_STEP.DONE && (
        <section data-testid="picking-done" className="space-y-3 rounded-3xl border border-border bg-white p-4 text-center dark:bg-[#1a1a1a]">
          <CheckCircle2 size={36} className="mx-auto text-ninja-green" />
          <p className="text-lg font-bold text-ninja-dark dark:text-white">
            {Number(order.pending_units) === 0 ? 'Orden pickeada' : 'No queda nada que se pueda pickear'}
          </p>
          {stuck.length > 0 && (
            <div data-testid="picking-stuck" className="space-y-1 text-sm text-text-secondary">
              <p>
                {stuck.length === 1 ? '1 línea sin stock' : `${stuck.length} líneas sin stock`}: avisa a tu supervisor.
              </p>
              {stuck.map((item) => (
                <p key={item.line_id}>
                  {item.sku}: faltan {formatQuantity(item.pending)} un
                </p>
              ))}
            </div>
          )}
          <OperatorActionBar>
            <Button variant="ghost" size="lg" onClick={() => navigate('/picking')} data-testid="picking-next-order">
              Siguiente orden
            </Button>
            {flows.includes(OPERATOR_FLOW.PACKING) && Number(order.pending_units) === 0 ? (
              <Button variant="primary" size="lg" onClick={() => navigate(`/packing/${order.id}`)} data-testid="picking-to-packing">
                Empacar
              </Button>
            ) : (
              <Button variant="primary" size="lg" onClick={() => navigate('/picking')}>
                Volver a la cola
              </Button>
            )}
          </OperatorActionBar>
        </section>
      )}

      {line && walk.step !== PICKING_STEP.DONE && (
        <section data-testid="pick-line" data-line-id={line.line_id} className="space-y-1 rounded-3xl border-2 border-ninja-green bg-white p-4 dark:bg-[#1a1a1a]">
          <p className="text-2xl font-bold text-ninja-dark dark:text-white">
            <CodeText text={line.sku} />
          </p>
          <p className="text-sm text-text-secondary">{line.name}</p>
          <p className="text-base font-semibold text-ninja-dark dark:text-white">
            Faltan {formatQuantity(pendingOf(line))} de {formatQuantity(line.quantity)} un
          </p>
          {target && (
            // At the slot step the slot's testid is its button's, below.
            <p
              data-testid={walk.step === PICKING_STEP.SLOT ? undefined : 'pick-slot-target'}
              className="flex items-center gap-1.5 pt-1 text-lg font-bold text-ninja-green"
            >
              <MapPin size={18} /> <CodeText text={target.slot_name || target.slot_code} />
              {target.available != null && (
                <span className="text-sm font-semibold text-text-secondary">· hay {formatQuantity(target.available)} un</span>
              )}
            </p>
          )}
          {/* What the step asks for, said as what the operator did: it reads the code, as scanning it would. */}
          {target && walk.step === PICKING_STEP.SLOT && (
            <div className="pt-2">
              <Button
                variant="primary"
                size="lg"
                fullWidth
                icon={MapPin}
                disabled={!accepting}
                onClick={() => tap(target.slot_code)}
                data-testid="pick-slot-target"
                data-code={target.slot_code}
              >
                <span>
                  <CodeText text={`Ya estoy en ${target.slot_name || target.slot_code}`} />
                </span>
              </Button>
            </div>
          )}
          {walk.step === PICKING_STEP.PRODUCT && (
            <div className="pt-2">
              <Button
                variant="primary"
                size="lg"
                fullWidth
                icon={PackageCheck}
                disabled={!accepting}
                onClick={() => tap(unitCode(line.codes, line.sku))}
                data-testid="pick-line-product"
                data-code={unitCode(line.codes, line.sku)}
              >
                <span>
                  <CodeText text={`Ya tengo ${line.sku}`} />
                </span>
              </Button>
            </div>
          )}
        </section>
      )}

      {/* One camera for the slot and the product: restarting it between them left the operator waiting. */}
      {line && atScanStep && (
        <>
          <OperatorScanPanel
            prompt={
              atSlot ? `Ve a ${target?.slot_name || target?.slot_code} y escanea la ubicación` : `Escanea ${line.sku}`
            }
            hint={atSlot ? target && 'O tócala arriba.' : `En ${target?.slot_name || target?.slot_code}. O tócalo arriba.`}
            accepting={accepting}
            lastCode={picking.scan.lastCode}
            rereadOn={atSlot ? null : `${line.line_id}:${target?.slot_code}`}
            busyLabel="Registrando…"
            onCode={read}
          />
          <OperatorActionBar>
            {atSlot ? (
              <Button variant="ghost" size="lg" icon={SkipForward} onClick={() => dispatch(pickingLineSkipped())} data-testid="pick-skip">
                Saltar
              </Button>
            ) : (
              <Button variant="ghost" size="lg" onClick={() => dispatch(pickingStepBack())}>
                Atrás
              </Button>
            )}
            <Button
              variant="secondary"
              size="lg"
              disabled={atSlot && !target}
              onClick={() => setSheet('short')}
              data-testid="pick-not-there"
            >
              No está
            </Button>
          </OperatorActionBar>
        </>
      )}

      {line && walk.step === PICKING_STEP.QUANTITY && walk.product && (
        <PickForm
          key={`${line.line_id}:${walk.product.code}:${target?.slot_code}`}
          orderId={id}
          line={line}
          target={target}
          product={walk.product}
          onBack={() => dispatch(pickingStepBack())}
        />
      )}

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-secondary">Líneas</h2>
          {hasPicked && (
            <Button variant="ghost" size="sm" icon={Undo2} onClick={() => setSheet('unpick')} data-testid="picked-lines">
              Devolver pickeado
            </Button>
          )}
        </div>
        {choosable.length > 0 && <p className="text-xs text-text-secondary">Toca otra para ir a ella.</p>}
        <ul className="space-y-2">
          {order.lines.map((item) => {
            const current = item.line_id === walk.lineId;
            const content = (
              <span className="flex items-center gap-3">
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold text-ninja-dark dark:text-white">{item.sku}</span>
                  <span className="block truncate text-sm font-normal text-text-secondary">{item.name}</span>
                </span>
                {pendingOf(item) > 0 && !firstTarget(item) && (
                  <span className="shrink-0 text-xs font-semibold text-amber-700 dark:text-amber-300">Sin stock</span>
                )}
                <span className="shrink-0 text-sm font-semibold text-ninja-dark dark:text-white">
                  {formatQuantity(item.prepared_quantity)} / {formatQuantity(item.quantity)}
                </span>
                {pendingOf(item) === 0 && <CheckCircle2 size={20} className="shrink-0 text-ninja-green" />}
              </span>
            );
            return (
              <li
                key={item.line_id}
                data-testid="picking-line"
                data-line-id={item.line_id}
                data-pending={item.pending}
                data-current={String(current)}
              >
                {choosable.includes(item) ? (
                  <Button
                    variant="choice"
                    disabled={!accepting}
                    onClick={() => dispatch(pickingLineChosen(item.line_id))}
                    data-testid="picking-line-choose"
                    data-line-id={item.line_id}
                  >
                    {content}
                  </Button>
                ) : (
                  <div
                    className={clsx(
                      'rounded-2xl border p-4',
                      current
                        ? 'border-ninja-green bg-ninja-greenLight dark:bg-[#1f2b24]'
                        : 'border-border bg-white dark:bg-[#1a1a1a]'
                    )}
                  >
                    {content}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {sheet === 'short' && line && target && (
        <ShortPickSheet isOpen onClose={() => setSheet(null)} orderId={id} line={line} target={target} product={walk.product} />
      )}
      {sheet === 'unpick' && <UnpickSheet isOpen onClose={() => setSheet(null)} orderId={id} lines={order.lines} />}

      <CandidatesSheet
        isOpen={resolution?.kind === 'ambiguous'}
        message={resolution?.message}
        options={resolution?.options}
        onChoose={(choice) => dispatch(choosePickingOption(choice))}
        onClose={() => dispatch(pickingResolutionDismissed())}
        busy={Boolean(pending)}
      />
    </div>
  );
};
